import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AllDataBreeding, UnassignedDevices } from "../../../../api/breeding";
import { DeviceWithoutLegacyPassword } from "../../../../generated/types";
import { useAccountStorage } from "../../../../hooks/useAccountStorage";
import { sectionToIcon } from "../../../type/default";
import { AttachDeviceModal } from "./AttachDeviceModal";
import { BuildingDevicesList } from "./BuildingDevicesList";
import { DetailCard } from "./DetailCard";

export const breedingNamesOf = (breedings: AllDataBreeding[]) =>
  new Map(breedings.map((entry) => [entry.breeding.id, entry.breeding.name]));

/** La vigebox ne s'installe pas dans un bâtiment (règle reprise de l'API). */
const canGoInBuilding = (device: DeviceWithoutLegacyPassword) =>
  device.type !== "vigebox";

/**
 * Produits rattachés à aucun bâtiment : sans cette carte, ils n'apparaîtraient sur aucun
 * élevage. Un installateur les voit regroupés par client. Chacun peut y être rattaché à un
 * bâtiment, sauf par un collaborateur éleveur, qui n'associe pas de produit.
 */
export const UnassignedDevicesCard = ({
  groups,
  showCompany,
  breedingNames,
  onAttached,
}: {
  groups: UnassignedDevices[];
  showCompany: boolean;
  breedingNames: Map<number, string>;
  onAttached: () => void;
}) => {
  const { t } = useTranslation();
  const { getAccountStorage } = useAccountStorage();
  const canAttach = getAccountStorage()?.type !== "breeder_assistant";
  const [attaching, setAttaching] = useState<{
    device: DeviceWithoutLegacyPassword;
    companyId: number;
  }>();

  if (groups.length === 0) return null;

  return (
    <DetailCard
      title={t("breeding-unassigned-devices-title")}
      icon={sectionToIcon.section_devices}
    >
      <p className="text-sm text-grey-medium mb-3">
        {t("breeding-unassigned-devices-hint")}
      </p>
      <div className="space-y-4">
        {groups.map((group) => (
          <div key={group.companyId}>
            {showCompany && (
              <h3 className="text-sm font-oxaniumMedium text-grey-medium mb-2">
                {group.companyName}
              </h3>
            )}
            <BuildingDevicesList
              devices={group.devices}
              renderAction={(device) =>
                canAttach &&
                canGoInBuilding(device) && (
                  <button
                    type="button"
                    onClick={() =>
                      setAttaching({ device, companyId: group.companyId })
                    }
                    title={t("device-attach-building")}
                    aria-label={t("device-attach-building")}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-secondary hover:text-tertiary"
                  >
                    <FontAwesomeIcon icon="link" />
                  </button>
                )
              }
            />
          </div>
        ))}
      </div>
      {attaching && (
        <AttachDeviceModal
          device={attaching.device}
          companyId={attaching.companyId}
          breedingNames={breedingNames}
          onClose={() => setAttaching(undefined)}
          onAttached={() => {
            setAttaching(undefined);
            onAttached();
          }}
        />
      )}
    </DetailCard>
  );
};
